import { useDebouncedValue } from "@mantine/hooks";
import { useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { ZodType } from "zod";

/**
 * Half-filled intake, kept where the merchant left it.
 *
 * A booking form is not a page you visit once. The merchant looks up a
 * tracking number, checks yesterday's settlement, answers a WhatsApp — and
 * every one of those is a route change that unmounts the form. Losing eleven
 * fields to a click on the nav is the kind of thing that makes people stop
 * trusting a tool and go back to the spreadsheet, so the draft outlives the
 * component: it is written to localStorage as they type and read back when
 * they return.
 *
 * This is local scratch, not state. The database still learns about the parcel
 * exactly once, when it is booked; a draft is cleared the moment that succeeds.
 * Nothing here is authoritative and nothing here is synced — a draft is one
 * browser's memory of what someone was in the middle of typing.
 *
 * Three things guard what comes back out:
 *
 *   - **A schema.** Restored data goes through the same zod schema the form
 *     declares, so a draft written by an older build fails to parse and is
 *     dropped rather than restored into a shape the form no longer renders.
 *   - **A version.** `VERSION` is the coarse escape hatch for when a form's
 *     meaning changes without its shape changing.
 *   - **An age.** A fortnight-old draft is not work in progress, and its COD
 *     amount is likely wrong. Anything past `MAX_AGE_MS` is discarded on read.
 *
 * Drafts are keyed per merchant (`draftKey`) so an admin booking for one
 * merchant never sees another's half-typed customer, and cleared wholesale on
 * sign-out — the browser is shared even when the account is not.
 */

const PREFIX = "blackarrow.draft.";

/** Bump to invalidate every stored draft at once. */
const VERSION = 1;

const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Long enough that a burst of typing is one write, short enough that a draft
 * survives the click that navigates away mid-sentence.
 */
const WRITE_DELAY_MS = 500;

interface Envelope {
  version: number;
  saved_at: number;
  value: unknown;
}

export interface StoredDraft<T> {
  value: T;
  savedAt: Date;
}

/**
 * One key per form per merchant.
 *
 * `null` is the signed-in merchant on their own portal, mirroring
 * `IntakeMerchant`. An admin's drafts are namespaced by the merchant they were
 * booking for, which is what keeps a switch of merchant from carrying a
 * half-typed address to the wrong customer.
 */
export function draftKey(form: string, merchantId: number | null): string {
  const scope = merchantId === null ? "self" : `m${String(merchantId)}`;

  return `${PREFIX}${form}.${scope}`;
}

export function clearDraft(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* private mode — there was nothing stored to remove */
  }
}

export function writeDraft<T>(key: string, value: T): void {
  const envelope: Envelope = {
    version: VERSION,
    saved_at: Date.now(),
    value,
  };

  try {
    localStorage.setItem(key, JSON.stringify(envelope));
  } catch {
    // Private mode, or the quota is full. The form still works; it just
    // stops surviving a navigation, which is not worth interrupting anyone
    // mid-order to say.
  }
}

/**
 * The stored draft, or null if there isn't a usable one.
 *
 * Anything unusable is deleted rather than left to fail the same way on every
 * mount — a key that can never parse is a key that should not still be there.
 */
export function readDraft<T>(
  key: string,
  schema: ZodType<T>,
): StoredDraft<T> | null {
  let raw: string | null = null;

  try {
    raw = localStorage.getItem(key);
  } catch {
    return null;
  }

  if (raw === null) {
    return null;
  }

  let envelope: unknown;

  try {
    envelope = JSON.parse(raw);
  } catch {
    clearDraft(key);
    return null;
  }

  if (typeof envelope !== "object" || envelope === null) {
    clearDraft(key);
    return null;
  }

  const { version, saved_at: savedAt, value } = envelope as Partial<Envelope>;

  if (version !== VERSION || typeof savedAt !== "number") {
    clearDraft(key);
    return null;
  }

  if (Date.now() - savedAt > MAX_AGE_MS) {
    clearDraft(key);
    return null;
  }

  const parsed = schema.safeParse(value);

  if (!parsed.success) {
    clearDraft(key);
    return null;
  }

  return { value: parsed.data, savedAt: new Date(savedAt) };
}

/**
 * Every draft in this browser, gone.
 *
 * Called on sign-out. localStorage is per-origin, not per-account, so without
 * this the next person to sign in on a shared back-office machine would be
 * handed the previous merchant's customer name and address in a form.
 */
export function clearAllDrafts(): void {
  try {
    const keys: string[] = [];

    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);

      if (key !== null && key.startsWith(PREFIX)) {
        keys.push(key);
      }
    }

    for (const key of keys) {
      localStorage.removeItem(key);
    }
  } catch {
    /* private mode — nothing was stored to begin with */
  }
}

/**
 * Structural equality, for telling "nothing has been typed yet" from a draft
 * worth keeping.
 *
 * Key order is not significant, so it gives the same answer for a value that
 * has been through `JSON.parse` as for one built by the form. Written this way
 * rather than as a hand-listed field comparison because the field a form grows
 * next is exactly the one a hand-listed check forgets — and forgetting it here
 * means calling a draft empty and deleting somebody's work.
 */
export function equals(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true;
  }

  if (Array.isArray(a) || Array.isArray(b)) {
    return (
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((item, index) => equals(item, b[index]))
    );
  }

  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) {
    return false;
  }

  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const keys = Object.keys(left);

  return (
    keys.length === Object.keys(right).length &&
    keys.every((key) => key in right && equals(left[key], right[key]))
  );
}

export interface DraftControls {
  /** When the restored draft was last written; null if nothing was restored. */
  restoredAt: Date | null;
  /** Throw the draft away and go back to blank. */
  clear: () => void;
}

interface DraftOptions<T> {
  /** From `draftKey`. Must be stable for the life of the component — the two
   *  intake forms are remounted (`key={merchant.id}`) when it would change. */
  key: string;
  /** Validates what comes back out of storage. */
  schema: ZodType<T>;
  /** A fresh, untouched value. Must be a stable reference (module scope). */
  blank: () => T;
  /** True when there is nothing worth keeping. Stable reference (module
   *  scope). An empty draft is deleted rather than stored, which is what makes
   *  booking and clearing tidy up after themselves. */
  isEmpty: (value: T) => boolean;
}

/**
 * `useState`, with the value parked in localStorage between visits.
 *
 * Drop-in: the returned setter takes the same updater or value `useState`
 * does. The write is debounced, so holding a key does not mean a write per
 * keystroke, and an empty value clears the key instead of storing a blank
 * form.
 */
export function useDraft<T>(
  options: DraftOptions<T>,
): [T, Dispatch<SetStateAction<T>>, DraftControls] {
  const { key, schema, blank, isEmpty } = options;

  // Read exactly once. Re-reading on any later render would fight whoever is
  // typing, and the store is only ever written by this hook anyway.
  const [restored] = useState(() => readDraft(key, schema));
  const [value, setValue] = useState<T>(() => restored?.value ?? blank());
  const [restoredAt, setRestoredAt] = useState<Date | null>(
    restored?.savedAt ?? null,
  );

  // What is currently in the store, so restoring a draft and then leaving it
  // alone does not rewrite it — otherwise `saved_at` would refresh on every
  // visit and a draft could never age out.
  const written = useRef<string | null>(
    restored === null ? null : JSON.stringify(restored.value),
  );

  const [settled] = useDebouncedValue(value, WRITE_DELAY_MS);

  useEffect(() => {
    if (isEmpty(settled)) {
      if (written.current !== null) {
        clearDraft(key);
        written.current = null;
      }

      return;
    }

    const json = JSON.stringify(settled);

    if (json === written.current) {
      return;
    }

    writeDraft(key, settled);
    written.current = json;
  }, [key, settled, isEmpty]);

  // The debounce is what makes typing cheap; it is also what would swallow the
  // last half-second of it. Leaving the page is precisely the moment being
  // protected against, so the pending value is flushed on the way out.
  const latest = useRef(value);

  useEffect(() => {
    latest.current = value;
  }, [value]);

  useEffect(
    () => () => {
      const pending = latest.current;

      if (isEmpty(pending)) {
        return;
      }

      const json = JSON.stringify(pending);

      if (json !== written.current) {
        writeDraft(key, pending);
      }
    },
    [key, isEmpty],
  );

  const clear = useCallback(() => {
    clearDraft(key);
    written.current = null;
    setRestoredAt(null);
    setValue(blank());
  }, [key, blank]);

  return [value, setValue, { restoredAt, clear }];
}
